import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "./app/store";
import { openapi } from "../openapi";
import {
  ACTIVE_MODULE_KEYS,
  IMultiplacerModuleKey,
  IProjectPublic,
} from "@multiconsult-group/multiplacer-core";
import { selectHttpCurrentUser } from "./userSelectors";
import { IS_IN_DEV } from "./flags";
import {
  getVisibleModulesForProject,
  IModuleDefinition,
} from "@multiconsult-group/multiplacer-core";

const selectProjectNo = (state: IRootState) => state.design.projectNo;

const idToString = (id: unknown): string | null => {
  if (id == null) return null;
  if (typeof id === "string" || typeof id === "number") return String(id);
  if (typeof id === "object") {
    const maybeId = id as { $oid?: unknown; _id?: unknown; toHexString?: () => string };
    if (typeof maybeId.$oid === "string") return maybeId.$oid;
    if (typeof maybeId.toHexString === "function") return maybeId.toHexString();
    if (maybeId._id != null) return idToString(maybeId._id);
  }
  return String(id);
};

export const selectIsCurrentUserAccessReady = createSelector(
  [(state: IRootState) => state],
  (state) => {
    const result = openapi.endpoints.getAuthMe.select(undefined)(state);
    return result.data != null;
  }
);

export const selectHttpCurrentProjct = createSelector(
  [selectProjectNo, (state: IRootState) => state],
  (projectNo, state) => {
    const args = {
      projectNo: Number(projectNo),
    };

    return openapi.endpoints.getMultiplacerProjectsSingleProject.select(args)(state)
      .data as IProjectPublic;
  }
);
export const selectHttpProject = (state: IRootState) => {
  const projectNo = selectProjectNo(state);
  return openapi.endpoints.getMultiplacerProjectsSingleProject.select({ projectNo })(state).data;
};

const EMPTY_PROJECTS: IProjectPublic[] = [];
export const selectHttpProjects = (state: IRootState) => {
  return openapi.endpoints.getMultiplacerProjects.select()(state).data || EMPTY_PROJECTS;
};

export const selectAvailableProjectsForUser = createSelector(
  [selectHttpProjects, selectHttpCurrentUser],
  (projects, currentUser) => {
    if (!currentUser) {
      return projects;
    }
    if (currentUser.isExternal) {
      // Eksterne brukere ser kun prosjekter de er medlem av
      const currentUserId = idToString(currentUser._id);
      return projects.filter((project) =>
        project.members.some((member) => idToString(member.userId) === currentUserId)
      );
    } else {
      // Interne brukere ser alle prosjekter
      return projects;
    }
  }
);

export const selectMemberForCurrentUserInCurrentProject = createSelector(
  [selectHttpProject, selectHttpCurrentUser],
  (project, currentUser) => {
    if (!project || !currentUser) {
      return null;
    }
    const currentUserId = idToString(currentUser._id);
    return project.members.find((member) => idToString(member.userId) === currentUserId) || null;
  }
);

export const selectPermissionsForCurrentUserInCurrentProject = createSelector(
  [selectMemberForCurrentUserInCurrentProject],
  (member) => {
    if (!member) {
      return null;
    }
    return member.permissions;
  }
);

type IModuleAccessLevel = "READ" | "WRITE";

const hasAnyAccess = (access?: IModuleAccessLevel) => access !== undefined;

export const selectVisibleModulesForCurrentProject = createSelector(
  [selectProjectNo],
  (projectNo): readonly IModuleDefinition[] => getVisibleModulesForProject(projectNo, IS_IN_DEV)
);

export const selectVisibleModuleKeysForCurrentProject = createSelector(
  [selectVisibleModulesForCurrentProject],
  (modules): IMultiplacerModuleKey[] => modules.map((module) => module.key)
);

export const selectAvailableModulesForCurrentUserInCurrentProject = createSelector(
  [
    selectHttpCurrentUser,
    selectPermissionsForCurrentUserInCurrentProject,
    selectVisibleModuleKeysForCurrentProject,
  ],
  (currentUser, permissions, visibleModuleKeys) => {
    if (IS_IN_DEV) {
      return visibleModuleKeys;
    }

    if (currentUser?.isExternal === false) {
      return visibleModuleKeys;
    }

    const moduleAccessByKey = new Map<IMultiplacerModuleKey, IModuleAccessLevel>(
      (permissions?.modules ?? []).map((m) => [
        m.module as IMultiplacerModuleKey,
        m.access as IModuleAccessLevel,
      ])
    );

    return visibleModuleKeys.filter((moduleKey) => hasAnyAccess(moduleAccessByKey.get(moduleKey)));
  }
);
